import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
  ReactNode,
  Dispatch,
  SetStateAction
} from "react";
import { FileService } from "../services/files-service";



export interface AttachmentItem {
  id: string;
  file: File;
  previewUrl: string;
  uploadedUrl?: string;
  isUploading: boolean;
  error?: boolean;
}

interface FileUploadContextType {
  attachments: AttachmentItem[];
  isBigDuration: boolean
  seIsBigDuration: Dispatch<SetStateAction<boolean>>
  processFiles: (files: File[], token: string) => void;
  removeAttachment: (id: string) => void;
  clearAttachments: () => void;
}



const FileUploadContext = createContext<FileUploadContextType | null>(null);



export function FileUploadProvider({ children }: { children: ReactNode }) {
  const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
  const [isBigDuration, seIsBigDuration] = useState(false)


  const updateAttachment = useCallback(
    (id: string, updates: Partial<AttachmentItem>) => {
      setAttachments(prev =>
        prev.map(att => (att.id === id ? { ...att, ...updates } : att))
      );
    },
    []
  );

  const addAttachment = useCallback((attachment: AttachmentItem) => {
    setAttachments(prev => [...prev, attachment]);
  }, []);

  const removeAttachment = useCallback((id: string) => {
    setAttachments(prev => {
      const found = prev.find(a => a.id === id);
      if (found) URL.revokeObjectURL(found.previewUrl);
      return prev.filter(a => a.id !== id);
    });
  }, []);

  const clearAttachments = useCallback(() => {
    setAttachments(prev => {
      prev.forEach(a => URL.revokeObjectURL(a.previewUrl));
      return [];
    });
  }, []);


  const triggerUpload = useCallback(
    async (file: File, attachmentId: string, token: string) => {
      try {
        const { get, post } = await FileService.getUploadUrl(
          file.name,
          file.type,
          token
        );

        if (!post) throw new Error("No upload URL returned");

        await FileService.uploadFile(post, file, token);

        const finalUrl = get ?? post.split("?")[0];

        updateAttachment(attachmentId, {
          isUploading: false,
          uploadedUrl: finalUrl,
          error: false
        });

      } catch (error) {
        console.error("Upload failed:", error);

        updateAttachment(attachmentId, {
          isUploading: false,
          error: true
        });
      }
    },
    [updateAttachment]
  );

  const MAX_VIDEO_DURATION = 300; // 2 minuto
  const processFiles = useCallback(async (files: File[], token: string) => {
    for (const file of files) {

      // if (file.type.startsWith("video/")) {
      //   try {
      //     const duration = await getVideoDuration(file);

      //     if (duration > MAX_VIDEO_DURATION) {
      //       addAttachment({
      //         id: crypto.randomUUID(),
      //         file,
      //         previewUrl: URL.createObjectURL(file),
      //         isUploading: false,
      //         error: true,
      //       });
      //       seIsBigDuration(true)
      //       console.warn("Vídeo excede duração permitida");
      //       continue;
      //     }

      //   } catch (err) {
      //     console.error(err);
      //     continue;
      //   }
      // }


      const id = crypto.randomUUID();
      const previewUrl = URL.createObjectURL(file);

      addAttachment({
        id,
        file,
        previewUrl,
        isUploading: true,
        error: false,
      });

      triggerUpload(file, id, token);
    }
  }, [addAttachment, triggerUpload]);


  useEffect(() => {
    return () => {
      attachments.forEach(a => URL.revokeObjectURL(a.previewUrl));
    };
  }, [attachments]);

  return (
    <FileUploadContext.Provider
      value={{
        attachments,
        isBigDuration,
        processFiles,
        removeAttachment,
        clearAttachments,
        seIsBigDuration
      }}
    >
      {children}
    </FileUploadContext.Provider>
  );
}

export function useFileUpload() {
  const context = useContext(FileUploadContext);

  if (!context)
    throw new Error("useFileUpload must be used inside FileUploadProvider");

  return context;
}

